import React from 'react';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import type { ViewMode } from '../../domain/types';
import { BrandIcon } from '../../ui/common';
import { VIEW_TITLES } from './mainLayoutHelpers';
import { useRightWorkspaceSidebar } from './RightWorkspaceSidebar';

interface MainWorkspaceHeaderProps {
  view: ViewMode;
  isCompact: boolean;
  isDrawerOpen: boolean;
  inspectorSheetOpen: boolean;
  chatSheetOpen?: boolean;
  onOpenDrawer: (trigger: HTMLElement) => void;
  onOpenSearch: () => void;
}

export function CopyViewUrlButton({ compact = false }: { compact?: boolean }): React.ReactElement {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* Clipboard permissions are optional. */
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={copied ? 'URL copiada' : 'Copiar URL de esta vista'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: compact ? '8px 10px' : '6px 12px',
        borderRadius: 8,
        background: 'var(--z-fill)',
        border: 'none',
        color: 'var(--z-muted)',
        fontSize: 12,
        cursor: 'pointer',
        minHeight: 36,
      }}
    >
      <span aria-hidden="true">🔗</span>
      {compact ? null : copied ? 'URL copiada' : 'Copiar URL'}
    </button>
  );
}

function SyncPill({ isCompact }: { isCompact: boolean }): React.ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: isCompact ? '6px 8px' : '6px 12px',
        borderRadius: 999,
        background: 'var(--z-ok-bg)',
        color: 'var(--z-ok-ink)',
        fontSize: 11.5,
        fontWeight: 600,
      }}
      title="Sincronizado"
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--z-ok)',
          animation: 'zpulse 2s infinite ease-in-out',
        }}
      />
      <span className="z-header-sync-label">Sincronizado</span>
    </div>
  );
}

function RightWorkspaceSidebarToggle({
  isCompact,
}: {
  isCompact: boolean;
}): React.ReactElement | null {
  const sidebar = useRightWorkspaceSidebar();
  if (!sidebar.hasSidebar) return null;

  // Desktop cycles open <-> minimized (a persistent icon rail); compact has
  // no rail, so "not open" always means fully closed. Either way the button
  // is the single open/close control — restore()/minimize() cover both.
  const isOpen = sidebar.mode === 'open';
  const label = isCompact
    ? isOpen
      ? 'Cerrar panel contextual'
      : 'Abrir panel contextual'
    : isOpen
      ? 'Colapsar panel contextual'
      : 'Abrir panel contextual';
  return (
    <button
      type="button"
      data-testid="right-workspace-sidebar-toggle"
      className="z-header-sidebar-toggle"
      aria-label={label}
      aria-expanded={isOpen}
      title={label}
      onClick={() => (isOpen ? sidebar.minimize() : sidebar.restore())}
    >
      {isOpen ? (
        <PanelRightClose size={16} strokeWidth={1.8} />
      ) : (
        <PanelRightOpen size={16} strokeWidth={1.8} />
      )}
    </button>
  );
}

export const MainWorkspaceHeader: React.FC<MainWorkspaceHeaderProps> = ({
  view,
  isCompact,
  isDrawerOpen,
  inspectorSheetOpen,
  chatSheetOpen = false,
  onOpenDrawer,
  onOpenSearch,
}) => {
  if (view === 'radar' && !isCompact) {
    return (
      <header
        data-testid="main-workspace-header"
        className="z-header"
        aria-label="Acciones del área de trabajo"
        style={{
          flex: '0 0 auto',
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '12px 0 12px 28px',
          background: 'var(--z-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CopyViewUrlButton />
          <RightWorkspaceSidebarToggle isCompact={isCompact} />
        </div>
      </header>
    );
  }
  const titles = VIEW_TITLES[view];
  const inert = inspectorSheetOpen || chatSheetOpen;

  return (
    <header
      data-testid="main-workspace-header"
      className="z-header"
      aria-hidden={inert ? true : undefined}
      {...(inert ? ({ inert: '' } as React.HTMLAttributes<HTMLElement>) : {})}
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0 10px 28px',
        background: 'var(--z-surface)',
        gap: 16,
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' }}
      >
        {isCompact ? (
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label="Abrir navegación"
            aria-expanded={isDrawerOpen}
            aria-controls="app-sidebar"
            aria-haspopup="dialog"
            onClick={(event) => onOpenDrawer(event.currentTarget)}
            style={{
              width: 40,
              height: 40,
              flex: '0 0 40px',
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--z-fill)',
              border: 'none',
              color: 'var(--z-text-2)',
              cursor: 'pointer',
            }}
          >
            <BrandIcon source="zunelo" size={26} />
          </button>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <h1
            style={{
              margin: 0,
              fontSize: isCompact ? 17 : 16,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--z-text)',
            }}
          >
            {titles.title}
          </h1>
          <span className="z-header-subtitle" style={{ fontSize: 12.5, color: 'var(--z-dim)' }}>
            {titles.subtitle}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <SyncPill isCompact={isCompact} />
        <CopyViewUrlButton compact={isCompact} />
        <button
          onClick={onOpenSearch}
          aria-label="Buscar"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: isCompact ? '8px 10px' : '6px 12px',
            borderRadius: 8,
            background: 'var(--z-fill)',
            border: 'none',
            color: 'var(--z-muted)',
            fontSize: 12,
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          {!isCompact ? <span>Buscar…</span> : null}
          <kbd
            className="z-header-search-kbd"
            style={{
              fontSize: 10,
              opacity: 0.6,
              background: 'var(--z-surface)',
              padding: '1px 4px',
              borderRadius: 4,
            }}
          >
            ⌘K
          </kbd>
        </button>
        <RightWorkspaceSidebarToggle isCompact={isCompact} />
      </div>
    </header>
  );
};
